import { DeesElement, customElement, property, html, css } from '@design.estate/dees-element';
import type { IKanbanTicket, IKanbanColumn } from '../interfaces.js';
import { kanbanStyles } from '../kanban.styles.js';
import { kanbanEvent, kanbanInitials, kanbanPriorityIcon } from '../kanban.helpers.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-kanban-card': DeesKanbanCard;
  }
}

@customElement('dees-kanban-card')
export class DeesKanbanCard extends DeesElement {
  @property({ attribute: false })
  accessor ticket: IKanbanTicket | undefined;

  @property({ attribute: false })
  accessor epic: IKanbanTicket | undefined;

  @property({ attribute: false })
  accessor subtasks: IKanbanTicket[] = [];

  @property({ attribute: false })
  accessor columns: IKanbanColumn[] = [];

  @property({ type: Boolean })
  accessor movable = false;

  @property({ type: Boolean })
  accessor moveDisabled = false;

  @property({ type: Boolean })
  accessor pending = false;

  @property({ type: Boolean })
  accessor saving = false;

  /** The assembled board owns pointer previews; standalone cards retain native drag events. */
  @property({ attribute: false })
  accessor pointerManaged = false;

  public static styles = [
    ...kanbanStyles,
    css`
      article {
        padding: 12px;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-primary);
        box-shadow: var(--dees-shadow-xs);
      }

      article:hover {
        border-color: var(--dees-color-border-strong);
      }

      article[aria-busy=true] > :not(.meta),
      article[aria-busy=true] .meta > :not(.saving) {
        opacity: .65;
      }

      article[data-movable=true] {
        cursor: grab;
      }

      .meta {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 24px;
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
      }

      .key {
        flex-shrink: 0;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
      }

      .link.epic {
        min-width: 0;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .link.epic:hover {
        color: var(--dees-color-link);
        text-decoration: underline;
      }

      .move {
        flex-shrink: 0;
        margin-left: auto;
      }

      .saving {
        margin-left: auto;
        font-size: var(--dees-font-caption1-size);
        white-space: nowrap;
      }

      .move::part(button) {
        width: 24px;
        height: 24px;
        min-height: 24px;
      }

      .title {
        display: block;
        width: 100%;
        border: 0;
        background: transparent;
        padding: 4px 0 0;
        color: inherit;
        text-align: left;
        cursor: pointer;
        font: inherit;
        font-weight: 600;
        line-height: 22px;
        overflow-wrap: anywhere;
      }

      .title:hover {
        color: var(--dees-color-link);
      }

      .footer {
        margin-top: 10px;
        gap: 6px;
      }

      .badge {
        padding: 1px 6px;
      }

      .assignee {
        display: flex;
        align-items: center;
        gap: 5px;
        margin-left: auto;
        min-width: 0;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size);
      }

      .assignee-name {
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .avatar {
        width: 20px;
        height: 20px;
        font-size: 9px;
      }

      details {
        border-top: 1px solid var(--dees-color-border-subtle);
        margin-top: 10px;
      }

      summary {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 9px 0 0;
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
        list-style: none;
      }

      summary::-webkit-details-marker {
        display: none;
      }

      summary .disclosure {
        margin-left: auto;
      }

      details[open] summary .disclosure {
        transform: rotate(90deg);
      }

      .subtask {
        display: flex;
        gap: 7px;
        align-items: center;
        padding-top: 8px;
        font-size: var(--dees-font-footnote-size);
      }

      .subtask dees-icon {
        flex-shrink: 0;
        color: var(--dees-color-text-secondary);
        font-size: 14px;
      }

      .subtask .link {
        color: var(--dees-color-text-primary);
      }

      .subtask .link:hover {
        color: var(--dees-color-link);
      }

      @media (pointer: coarse) {
        .move::part(button) {
          width: 32px;
          height: 32px;
          min-height: 32px;
        }
        summary {
          min-height: 36px;
        }
      }
    `,
  ];

  private open(ticketId: string): void {
    kanbanEvent(this, 'kanban-ticket-open', { ticketId });
  }

  public render() {
    const ticket = this.ticket;
    if (!ticket) return html``;
    const completed = this.subtasks.filter(task =>
      this.columns.some(column => column.id === task.columnId && column.done),
    ).length;
    return html`<article
      aria-busy=${String(this.pending)}
      data-movable=${String(this.movable && !this.moveDisabled && !this.pending)}
      draggable=${String(this.movable && !this.moveDisabled && !this.pending && !this.pointerManaged)}
      @dragstart=${(event: DragEvent) => {
        if (
          this.pointerManaged ||
          !this.movable ||
          this.moveDisabled ||
          this.pending ||
          !event.dataTransfer
        ) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.setData('application/x-dees-kanban-ticket', ticket.id);
        event.dataTransfer.effectAllowed = 'move';
        kanbanEvent(this, 'kanban-drag-start', { ticketId: ticket.id });
      }}
      @dragend=${() => {
        if (!this.pointerManaged) kanbanEvent(this, 'kanban-drag-end', {});
      }}
    >
      <div class="meta">
        <span class="key">${ticket.key}</span>
        ${this.epic
          ? html`<button
              class="link epic"
              title=${this.epic.title}
              @click=${() => this.open(this.epic!.id)}
            >${this.epic.title}</button>`
          : ''}
        ${this.saving
          ? html`<span class="saving" role="status">Saving…</span>`
          : this.movable
            ? html`<dees-button
                class="move"
                size="icon"
                type="ghost"
                icon="lucide:Ellipsis"
                text="Move ticket"
                .disabled=${this.pending || this.moveDisabled}
                @clicked=${(event: Event) =>
                  kanbanEvent(this, 'kanban-move-menu', {
                    ticketId: ticket.id,
                    anchor: event.currentTarget as HTMLElement,
                  })}
              ></dees-button>`
            : ''}
      </div>
      <button class="title" @click=${() => this.open(ticket.id)}>${ticket.title}</button>
      <div class="row footer">
        ${ticket.priority && ticket.priority !== 'none'
          ? html`<span class="priority" data-priority=${ticket.priority}><dees-icon
              .icon=${kanbanPriorityIcon(ticket.priority)}
              iconSize="13"
              aria-hidden="true"
            ></dees-icon>${ticket.priority}</span>`
          : ''}
        ${ticket.labels?.map(label => html`<span class="badge">${label}</span>`)}
        <span class="assignee" title=${ticket.assignee?.name ?? 'Unassigned'}>
          <span
            class="avatar"
            aria-hidden="true"
          >${ticket.assignee ? kanbanInitials(ticket.assignee.name) : '–'}</span>
          <span class="assignee-name">${ticket.assignee?.name ?? 'Unassigned'}</span>
        </span>
      </div>
      ${this.subtasks.length
        ? html`<details><summary><dees-icon
              icon="lucide:ListChecks"
              iconSize="14"
              aria-hidden="true"
            ></dees-icon>${completed}/${this.subtasks.length} subtasks complete<dees-icon
              class="disclosure"
              icon="lucide:ChevronRight"
              iconSize="14"
              aria-hidden="true"
            ></dees-icon></summary>
            ${this.subtasks.map(
              task => html`<div class="subtask">
                <dees-icon
                  .icon=${this.columns.some(column => column.id === task.columnId && column.done)
                    ? 'lucide:CircleCheck'
                    : 'lucide:Circle'}
                  iconSize="14"
                  aria-hidden="true"
                ></dees-icon>
                <button class="link" @click=${() => this.open(task.id)}>${task.title}</button>
              </div>`,
            )}</details>`
        : ''}
    </article>`;
  }
}
